import { BulkImportType } from '../../../../../types';
import HtmlPreview from '../../../../shared/html-preview';
import { useState } from 'react';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import SharedReviewLayout, {
  ReviewSection,
} from '../../../../shared/shared-review-layout';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import ReviewSectionBoxes from './review-section-boxes';
import BulkOpTag from './bulk-op-tag';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Star from '@mui/icons-material/Star';

interface StepReviewProps {
  selectedBulkOp: BulkOpsFormValues;
  bulkImportTypes: BulkImportType[];
  isDetail?: boolean;
  spreadSheetName?: string;
  isNewUi?: boolean;
}

export default function StepReview({
  selectedBulkOp,
  bulkImportTypes,
  isDetail,
  spreadSheetName,
  isNewUi = false,
}: StepReviewProps) {
  const [showAll, setShowAll] = useState(false);
  const columns = selectedBulkOp.columns || [];

  const MAX_CHIPS = 8;
  const hiddenCount = columns.length - MAX_CHIPS;

  const sourceType =
    selectedBulkOp.sourceType === 'User'
      ? ('User' as const)
      : selectedBulkOp.sourceType === 'Group'
      ? ('Group' as const)
      : selectedBulkOp.sourceType === 'gPanel_Role'
      ? ('gPanel Role' as const)
      : selectedBulkOp.sourceType === 'OrgUnit'
      ? ('OrgUnit' as const)
      : ('Domain' as const);

  const selectedBulkImportType = bulkImportTypes.find(
    (type) => type.name === selectedBulkOp.bulkImport
  )!;

  const bulkOpsSection: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftWidth: {
        labelWidth: !isDetail ? 3 : 6,
        valueWidth: !isDetail ? 9 : 6,
      },
      rightWidth: { labelWidth: 4, valueWidth: 8 },
      leftItems: [
        {
          label: 'Name',
          value: selectedBulkOp.name,
        },
        // Design: the new UI shows only the operation type's description,
        // labelled "Description", after the type.
        ...(isNewUi
          ? [
              {
                label: 'Bulk Import Type',
                value: selectedBulkImportType.niceName,
              },
              {
                label: 'Description',
                value: selectedBulkImportType.description,
              },
            ]
          : [
              {
                label: 'Description',
                value: selectedBulkOp.description,
                isVisible: !!(
                  selectedBulkOp.description &&
                  selectedBulkOp.description.length > 0
                ),
              },
              {
                label: 'Bulk Import Type',
                value: selectedBulkImportType.niceName,
              },
              {
                label: 'Operation Type Description',
                value: selectedBulkImportType.description,
              },
            ]),
        {
          label: 'Email Notifications',
          value: selectedBulkOp.notifyEmails?.map((target) => {
            return {
              email: target,
              type: 'User' as const,
            };
          }),
          variant: 'chip',
          flex: true,
          isVisible: !!(
            selectedBulkOp.notifyEmails &&
            selectedBulkOp.notifyEmails.length > 0
          ),
        },
      ],
      rightItems: [
        ...(isDetail
          ? [
              {
                label: 'Creator',
                value: [
                  { email: selectedBulkOp.createdBy, type: 'User' as const },
                ],
                variant: 'chip',
                isVisible: !!selectedBulkOp.createdBy,
              },
              {
                label: 'Created Date',
                value: selectedBulkOp.createdDate,
                variant: 'date',
                isVisible: !!(
                  selectedBulkOp.createdDate && selectedBulkOp.createdDate != 0
                ),
              },
              {
                label: 'Last Ran Date',
                value: selectedBulkOp.lastRan
                  ? selectedBulkOp.lastRan
                  : 'Never Ran',
                variant: 'date',
              },
            ]
          : []),
      ],
    },
    {
      title: 'Configuration',
      leftItems: [
        {
          label: 'Path',
          value:
            selectedBulkOp.path === 'guideMe'
              ? 'Guided Export'
              : 'Advanced Operation',
          isVisible: !!(selectedBulkOp.path && selectedBulkOp.path.length > 0),
        },
        ...(spreadSheetName && spreadSheetName.trim() !== ''
          ? [
              {
                label: 'Spreadsheet Name',
                value: spreadSheetName,
              },
            ]
          : [
              {
                label: 'Spreadsheet ID',
                value: selectedBulkOp.spreadsheetId,
                isVisible: !!(
                  selectedBulkOp.spreadsheetId &&
                  selectedBulkOp.spreadsheetId.length > 0
                ),
              },
            ]),
        {
          label: 'Resource Ids',
          value: (
            <Stack flexDirection="row" gap={1}>
              {selectedBulkOp.resourceIds?.map((resourceId) => {
                return (
                  <Chip
                    key={resourceId}
                    label={resourceId}
                    size="small"
                    avatar={<Avatar children={<Star />} />}
                  />
                );
              })}
            </Stack>
          ),
          isVisible: !!(
            selectedBulkOp.resourceIds && selectedBulkOp.resourceIds.length > 0
          ),
        },
        {
          label: 'Start Date',
          value: selectedBulkOp.startDate ? selectedBulkOp.startDate : 0,
          variant: 'date',
          isVisible: !!(
            selectedBulkOp.startDate && selectedBulkOp.startDate != 0
          ),
        },
        {
          label: 'End Date',
          value: selectedBulkOp.endDate ? selectedBulkOp.endDate : 0,
          variant: 'date',
          isVisible: !!(selectedBulkOp.endDate && selectedBulkOp.endDate != 0),
        },
      ],
    },
    {
      title: 'Columns',
      leftItems: [
        {
          label: isNewUi ? 'Selected Columns' : undefined,
          value: isNewUi ? (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {columns
                .slice(0, showAll ? columns.length : MAX_CHIPS)
                .map((c) => (
                  <BulkOpTag key={c.name} label={c.columnName} />
                ))}
            </Stack>
          ) : (
            columns
              ?.slice(0, showAll ? columns.length : MAX_CHIPS)
              .map((c) => ({ email: c.columnName, type: 'Unknown' as const }))
          ),
          variant: isNewUi ? undefined : 'chip',
          isVisible: !!(
            selectedBulkOp.columns && selectedBulkOp.columns.length > 0
          ),
        },
        {
          value: columns && columns.length > MAX_CHIPS && (
            <Box
              sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}
            >
              <Button size="small" onClick={() => setShowAll(!showAll)}>
                {showAll ? 'Show less' : `+${hiddenCount} more`}
              </Button>
            </Box>
          ),
          isVisible: !!(
            selectedBulkOp.columns && selectedBulkOp.columns.length > MAX_CHIPS
          ),
        },
      ],
    },
    {
      title: 'Sources',
      leftItems: [
        {
          label: 'Source Type',
          value: sourceType,
          isVisible: !!(
            selectedBulkOp.sourceType && selectedBulkOp.sourceType.length > 0
          ),
        },
        {
          label: isNewUi ? 'Selected Sources' : 'Sources',
          value:
            !selectedBulkOp.allSource && selectedBulkOp.sources
              ? selectedBulkOp.sources.map((target) => {
                  return {
                    email: target,
                    type: sourceType,
                  };
                })
              : 'All',
          variant: selectedBulkOp.allSource ? 'default' : 'chip',
          flex: selectedBulkOp.allSource ? false : true,
          isVisible: !!(
            selectedBulkOp.allSource ||
            (selectedBulkOp.sources && selectedBulkOp.sources.length > 0)
          ),
        },
      ],
    },
    {
      title: 'Group Configuration',
      leftWidth: {
        labelWidth: 3,
        valueWidth: 9,
      },
      leftItems: [
        {
          label: 'Included Nested Groups',
          value: selectedBulkOp.includeNestedGroups?.map((target) => {
            return {
              email: target,
              type: sourceType,
            };
          }),
          variant: 'chip',
          flex: true,
          isVisible: !!(
            selectedBulkOp.includeNestedGroups &&
            selectedBulkOp.includeNestedGroups.length > 0
          ),
        },
        {
          label: 'Auto Create Groups',
          value: selectedBulkOp.createGroups,
          flex: false,
          isVisible: selectedBulkOp.bulkImport === 'groupImport',
        },
      ],
    },
    ...(selectedBulkOp.bulkImport === 'usersVacationResponder'
      ? [
          {
            title: 'Vacation Responder',
            leftItems: [
              {
                label: 'Start Date',
                value: selectedBulkOp.vacationResponderData?.startTime
                  ? selectedBulkOp.vacationResponderData.startTime
                  : 0,
                variant: 'date',
                isVisible: !!selectedBulkOp.vacationResponderData?.startTime,
              },
              {
                label: 'End Date',
                value: selectedBulkOp.vacationResponderData?.endTime
                  ? selectedBulkOp.vacationResponderData.endTime
                  : 0,
                variant: 'date',
                isVisible: !!selectedBulkOp.vacationResponderData?.endTime,
              },
              {
                label: 'Subject',
                value:
                  selectedBulkOp.vacationResponderData?.responseSubject || '',
                isVisible:
                  !!selectedBulkOp.vacationResponderData?.responseSubject,
              },
              {
                label: '',
                value: (
                  <HtmlPreview
                    title="Vacation Responder Preview"
                    html={
                      selectedBulkOp.vacationResponderData?.responseBodyHtml ||
                      ''
                    }
                    width={550}
                  />
                ),
                flex: true,
                isVisible:
                  !!selectedBulkOp.vacationResponderData?.responseBodyHtml,
              },
            ],
          },
        ]
      : []),
  ];

  if (isNewUi) {
    return (
      <Stack gap={2} sx={{ width: '100%' }}>
        <FormSectionHeader
          descriptionVariant="body2"
          description="No changes have been made yet. Check the details below, then click Finish to run this bulk operation."
        >
          Review &amp; Confirm
        </FormSectionHeader>
        <ReviewSectionBoxes sections={bulkOpsSection} />
      </Stack>
    );
  }

  return <SharedReviewLayout sections={bulkOpsSection} />;
}
